import * as Sentry from '@sentry/react';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { createBrowserRouter, RouterProvider, useRouteError } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider';
import { PostHogIdentify } from './auth/PostHogIdentify';
import { AppLoadingFallback } from './components/patterns/AppLoadingFallback';
import { ThemeProvider } from './components/theme/ThemeProvider';
import { UserPreferencesProvider } from './hooks/useUserPreferences';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const MagicLinkPage = lazy(() => import('./pages/auth/MagicLinkPage'));
// Local-dev only. `import.meta.env.DEV` is a build-time constant, so a production build drops the
// route and the chunk.
const DevSignInPage = import.meta.env.DEV ? lazy(() => import('./pages/auth/DevSignInPage')) : null;
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'));
const AuthCallbackPage = lazy(() => import('./pages/auth/AuthCallbackPage'));
const AcceptInvitationPage = lazy(() => import('./pages/auth/AcceptInvitationPage'));
const PublicReimbursementIntakePage = lazy(() => import('./pages/PublicReimbursementIntakePage'));
const PublicReimbursementMoreInfoResponsePage = lazy(
  () => import('./pages/PublicReimbursementMoreInfoResponsePage'),
);
const ContractReviewExternalPortalPage = lazy(
  () => import('./pages/ContractReviewExternalPortalPage'),
);
const ContractReviewIntakePage = lazy(() => import('./pages/ContractReviewIntakePage'));
const ConnectMcpPage = lazy(() => import('./pages/ConnectMcpPage'));
const AppShell = lazy(() => import('./AppShell').then((m) => ({ default: m.AppShell })));

function RouteLoadingFallback() {
  return <AppLoadingFallback />;
}

function FallbackComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-foreground">Algo deu errado.</h1>
        <p className="mt-2 text-muted-foreground">
          Desculpe, mas algo inesperado aconteceu. Por favor, tente recarregar a página.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
        >
          Recarregar Página
        </button>
      </div>
    </div>
  );
}

function RouteErrorFallback() {
  const error = useRouteError();
  const reportedErrorRef = useRef<unknown>(undefined);

  useEffect(() => {
    if (reportedErrorRef.current === error) return;
    reportedErrorRef.current = error;
    Sentry.captureException(error);
  }, [error]);

  return <FallbackComponent />;
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <LoginPage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    // Public — user lands here from the "Esqueci minha senha" link on
    // /login. Asks for an email, posts to /identity/auth/forgot-password,
    // shows the anti-enumeration success card.
    path: '/auth/forgot-password',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    // Public — destination of the WorkOS-rendered "reset your password"
    // email link. Reads `?token=…`, asks for a new password, posts to
    // /identity/auth/reset-password.
    path: '/auth/reset-password',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <ResetPasswordPage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    // Public — passwordless sign-in via WorkOS magic auth. Two-step flow
    // (request → enter code → session).
    path: '/auth/magic-link',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <MagicLinkPage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  ...(DevSignInPage
    ? [
        {
          // Local-dev only — `/dev/sign-in?as=<email of a local user>&next=<path>` for AI agents
          // doing browser QA. Needs DEV_SIGN_IN_ENABLED=true on the API (dev-sign-in-policy.ts).
          path: '/dev/sign-in',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <DevSignInPage />
            </Suspense>
          ),
          errorElement: <RouteErrorFallback />,
        },
      ]
    : []),
  {
    // Public — completes a sign-in suspended with email_verification_required.
    // Reached from LoginPage on the structured 401 (carries the pending
    // token in router state) or as a stand-alone resend page.
    //
    // Reached by the login flow's automatic error handling, not by a
    // user-initiated CTA. Any WorkOS-managed user with
    // `emailVerified=false` lands here unconditionally.
    path: '/auth/verify-email',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <VerifyEmailPage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    // Public — destination of the API's GET /identity/auth/workos/callback
    // redirect after SSO / OAuth, also the magic-link redirect mode. Reads
    // the pre-mapped session params from the URL and primes localStorage
    // via the shared useStoreAuthSession hook.
    path: '/auth/callback',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <AuthCallbackPage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    // Public — destination of the WorkOS-rendered invitation email
    // link. Reads `?token=…`, validates the invitation, asks for name +
    // password, posts to /identity/auth/accept-invitation, primes the
    // session and redirects to /lab.
    //
    // Reached by an externally-triggered invitation email link.
    path: '/auth/accept-invitation',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <AcceptInvitationPage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/reimbursements/intake',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <PublicReimbursementIntakePage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    // Public portal that lets a requester complement an existing reimbursement
    // after a manager pressed "ask for more info" (ALC-800). The token is
    // single-purpose and is mapped to a server-side ReimbursementInfoRequest.
    path: '/reimbursements/respond/:token',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <PublicReimbursementMoreInfoResponsePage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/procurement/contract-reviews/external',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <ContractReviewExternalPortalPage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    // Public tracking page (canonical URL going forward; the legacy
    // /procurement/contract-reviews/external?token=... route stays alive for
    // back-compat with already-issued tokens).
    path: '/contract-review/track/:token',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <ContractReviewExternalPortalPage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    // Public anonymous intake form, branded by tenant slug.
    path: '/contract-review/intake/:tenantSlug',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <ContractReviewIntakePage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    // MCP "Standalone Connect" Login URI target. AuthKit redirects the user here
    // with `external_auth_id` so they can sign in with our app and pick which
    // tools to authorize for the connecting AI agent.
    path: '/mcp/connect',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <ConnectMcpPage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <AppShell />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
]);

function App() {
  return (
    <Sentry.ErrorBoundary fallback={FallbackComponent} showDialog>
      <AuthProvider>
        <UserPreferencesProvider>
          <ThemeProvider>
            <PostHogIdentify />
            <RouterProvider router={router} />
          </ThemeProvider>
        </UserPreferencesProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
